import { Post } from '@sportninja/common/hooks/useSocialFeed'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'

/**
 * Define the shape of the form state
 */
export interface IPostFormState {
  /** Whether the image upload section is visible */
  showImage: boolean
  /** Whether the link input section is visible */
  showLink: boolean
  /** Whether the send via (email/push) section is visible */
  showSendVia: boolean
  /** Whether to send the post via email */
  sendViaEmail: boolean
  /** Whether to send the post via push notification */
  sendViaPush: boolean
  /** Whether to publish the post to the social feed */
  postToFeed: boolean
  /** Whether users can reply to this post */
  canReply: boolean
  /** Whether to only active competitions */
  activeCompetition: boolean
  /** Array of role IDs that can see this post */
  visibleToRoles: string[]
  /** The post title */
  title: string
  /** The post content/body, now containing HTML */
  content: string
  /** Plain text version of the content without HTML formatting */
  body_plain_text: string
  /** Optional URL link to include in the post */
  url: string
  /** ID of the parent entity (organization, team, or schedule) */
  parentId: string
  /** Type of the parent entity */
  parentEntityType?: 'team' | 'schedule' | 'organization'
  /** Base64 encoded image data or image ID */
  image: string
  /** Whether the image has been changed (for edit mode) */
  imageChanged: boolean
  /** Currently selected entity ID */
  selected?: string
  /** Type of the currently selected entity */
  selectedType: string | null
  /** Number representing the target audience size */
  targetAudience: number | null
  /** Whether audience has been calculated for current entity */
  hasCalculatedAudience: boolean
  /** Last selected entity ID to track entity changes */
  lastSelectedEntity: string | undefined
  /** Whether the form is currently submitting */
  isLoading: boolean
  /** Current error message, if any */
  error: string | null
  /** Array of field names that have validation errors */
  fieldErrors: string[]
  /** Object mapping field names to their specific error messages */
  customError: { [key: string]: string }
}

/**
 * Define the initial state with default values for all form fields
 */
export const initialState: IPostFormState = {
  showImage: true, // ENABLE by default
  showLink: true, // ENABLE by default
  showSendVia: true,
  sendViaEmail: false, // DISABLED by default - opt-in required
  sendViaPush: false, // DISABLED by default - opt-in required
  postToFeed: false, // DISABLED by default - opt-in required
  canReply: false,
  activeCompetition: true,
  visibleToRoles: [],
  title: '',
  content: '',
  body_plain_text: '',
  url: '',
  parentId: '',
  parentEntityType: undefined,
  image: '',
  imageChanged: false,
  selected: undefined,
  selectedType: null,
  targetAudience: null,
  hasCalculatedAudience: false,
  lastSelectedEntity: undefined,
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: {},
}

/**
 * Define actions the reducer can handle for state management
 */
export type FormAction =
  | { type: 'SET_FIELD'; field: keyof IPostFormState; payload: any }
  | { type: 'RESET_FORM' }
  | { type: 'SET_POST_TO_EDIT'; payload: Post | null }
  | { type: 'TOGGLE_SECTION'; section: 'image' | 'link' | 'sendVia' }
  | {
      type: 'SET_VALIDATION_ERRORS'
      fieldErrors: string[]
      customError: { [key: string]: string }
    }
  | { type: 'CLEAR_VALIDATION_ERRORS' }
  | { type: 'SET_SEND_VIA'; sendType: 'email' | 'push'; value: boolean }

/**
 * Form reducer function to handle state updates in a predictable way
 * @param state - Current form state
 * @param action - Action object describing the state change
 * @returns Updated form state
 */
export function formReducer(
  state: IPostFormState,
  action: FormAction
): IPostFormState {
  switch (action.type) {
    case 'SET_FIELD': {
      const newState = { ...state, [action.field]: action.payload }

      // If postToFeed is being set to false, also set canReply to false
      if (action.field === 'postToFeed' && action.payload === false) {
        newState.canReply = false
      }

      // If selected entity changes, reset audience calculation flag
      if (
        action.field === 'selected' &&
        action.payload !== state.lastSelectedEntity
      ) {
        newState.hasCalculatedAudience = false
        newState.lastSelectedEntity = action.payload
      }

      // When content changes, extract plain text
      if (action.field === 'content') {
        // Strip HTML tags to create plain text version
        const plainText = action.payload
          .replace(/<[^>]*>/g, '') // Remove HTML tags
          .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
          .replace(/&lt;/g, '<') // Replace HTML entities
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .trim()
        newState.body_plain_text = plainText

        // Clear validation error for content on change
        if (newState.fieldErrors?.length) {
          newState.fieldErrors = newState.fieldErrors.filter(
            (f) => f !== 'content'
          )
        }
        if (newState.customError?.content) {
          const { content, ...rest } = newState.customError
          newState.customError = rest
        }
      }

      // If user updates target audience (visibleToRoles), clear its validation error
      if (action.field === 'visibleToRoles') {
        if (newState.fieldErrors?.length) {
          newState.fieldErrors = newState.fieldErrors.filter(
            (f) => f !== 'visibleToRoles'
          )
        }
        if (newState.customError?.visibleToRoles) {
          const { visibleToRoles, ...rest } = newState.customError
          newState.customError = rest
        }
      }

      // If user updates URL, clear its validation error (tracked as 'website')
      if (action.field === 'url') {
        if (newState.fieldErrors?.length) {
          newState.fieldErrors = newState.fieldErrors.filter(
            (f) => f !== 'website'
          )
        }
        if (newState.customError?.website) {
          const { website, ...rest } = newState.customError
          newState.customError = rest
        }
      }

      // Clear validation error for title on change
      if (action.field === 'title') {
        if (newState.fieldErrors?.length) {
          newState.fieldErrors = newState.fieldErrors.filter(
            (f) => f !== 'title'
          )
        }
        if (newState.customError?.title) {
          const { title, ...rest } = newState.customError
          newState.customError = rest
        }
      }

      return newState
    }

    case 'RESET_FORM':
      return { ...initialState }

    case 'SET_POST_TO_EDIT': {
      if (!action.payload) {
        return { ...initialState }
      }
      const post = action.payload
      const imageId = getImageThumbnailId(post)
      const postUrl = post.url
      return {
        ...state,
        image: imageId,
        showImage: Boolean(imageId),
        title: post.title,
        content: post.body,
        url: postUrl,
        showLink: Boolean(postUrl),
        parentId: post.parent_id,
        parentEntityType: post.parent_type as
          | 'team'
          | 'schedule'
          | 'organization',
        selected: post.parent_id,
        selectedType: post.parent_type,
        canReply: (post as any).can_reply || false,
        activeCompetition: (post as any).active_competition || false,
        visibleToRoles: ((post as any).role_types || []).map(
          (role: { id: number; name: string }) => String(role.id)
        ),
        postToFeed: true,
      }
    }

    case 'TOGGLE_SECTION':
      switch (action.section) {
        case 'image':
          return { ...state, showImage: !state.showImage }
        case 'link':
          return { ...state, showLink: !state.showLink }
        case 'sendVia':
          return { ...state, showSendVia: !state.showSendVia }
        default:
          return state
      }

    case 'SET_VALIDATION_ERRORS':
      return {
        ...state,
        fieldErrors: action.fieldErrors,
        customError: action.customError,
      }

    case 'CLEAR_VALIDATION_ERRORS':
      return {
        ...state,
        fieldErrors: [],
        customError: {},
      }

    case 'SET_SEND_VIA': {
      const newState = {
        ...state,
        [action.sendType === 'email' ? 'sendViaEmail' : 'sendViaPush']:
          action.value,
      }

      // Clear visible roles if both options are unchecked
      if (action.sendType === 'email' && !action.value && !state.sendViaPush) {
        newState.visibleToRoles = []
      }
      if (action.sendType === 'push' && !action.value && !state.sendViaEmail) {
        newState.visibleToRoles = []
      }

      return newState
    }

    default:
      return state
  }
}
